import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, X } from 'lucide-react';
import { toast } from 'sonner';

interface ClaimedImagesModalProps {
  open: boolean;
  onClose: () => void;
  images: Array<{
    id: string;
    image_url: string;
    prompt: string;
    model?: string;
  }>;
}

const ClaimedImagesModal = ({ open, onClose, images }: ClaimedImagesModalProps) => {
  const handleDownload = async (imageUrl: string, prompt: string) => {
    try {
      const link = document.createElement('a');
      link.download = `${prompt.substring(0, 30).replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.png`;
      
      // Handle data URLs directly without fetch
      if (imageUrl.startsWith('data:')) {
        link.href = imageUrl;
      } else {
        // For regular URLs, fetch and convert to blob
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        link.href = url;
      }
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Only revoke if we created a blob URL
      if (!imageUrl.startsWith('data:')) {
        window.URL.revokeObjectURL(link.href);
      }
      
      toast.success('Image downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-900/95 backdrop-blur-xl border border-white/20 shadow-2xl text-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            🎉 Welcome! Your Guest Images Have Been Claimed
          </DialogTitle>
          <DialogDescription className="text-gray-300">
            We've successfully transferred {images.length} image{images.length !== 1 ? 's' : ''} from your guest session to your account. 
            You can download them now or find them in your gallery later.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {images.map((image) => (
            <div key={image.id} className="relative group">
              <div className="aspect-square rounded-lg overflow-hidden bg-slate-800/50 border border-white/10">
                <img
                  src={image.image_url}
                  alt={image.prompt}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-slate-900/95 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                <Button
                  size="sm"
                  onClick={() => handleDownload(image.image_url, image.prompt)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
              {image.prompt && (
                <p className="text-xs text-gray-400 mt-2 line-clamp-2">
                  {image.prompt}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button onClick={onClose} className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0">
            Continue to Dashboard
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClaimedImagesModal;
